import type { ExtractedLink } from "../thread-context"

// btoa only takes Latin-1, so a link with "ü" or an emoji in it used to throw.
function urlKey(url: string): string {
	let binary = ""
	for (const byte of new TextEncoder().encode(url)) {
		binary += String.fromCharCode(byte)
	}
	return btoa(binary).replace(/[^a-zA-Z0-9]/g, "")
}

/**
 * Extracts external links from HTML content (both anchor tags and plain text URLs)
 * @param htmlContent The HTML content to parse
 * @returns Array of extracted links with their text and URLs
 */
export function extractLinksFromHTML(htmlContent: string): ExtractedLink[] {
	if (!htmlContent) {
		return []
	}

	const extractedLinks: ExtractedLink[] = []

	// Create a temporary div to parse HTML
	const tempDiv = document.createElement("div")
	tempDiv.innerHTML = htmlContent

	// First, find all anchor tags
	const anchorLinks = tempDiv.querySelectorAll("a[href]")
	anchorLinks.forEach((link, index) => {
		const href = link.getAttribute("href")
		const text = link.textContent?.trim() || href || ""

		// Only include external links (http/https)
		if (href && (href.startsWith("http://") || href.startsWith("https://"))) {
			const id = `anchor-${urlKey(href)}-${index}`
			extractedLinks.push({
				id,
				url: href,
				text,
			})
		}
	})

	// Then, extract plain text URLs using regex, but only from text nodes not inside anchor tags
	const urlRegex =
		/https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b([-a-zA-Z0-9()@:%_+.~#?&//=]*)/g
	const plainTextUrls: string[] = []

	// Walk through text nodes to find URLs not in anchor tags
	const walker = document.createTreeWalker(tempDiv, NodeFilter.SHOW_TEXT, {
		acceptNode: (node) => {
			// Check if the text node's parent is an anchor tag
			let parent = node.parentElement
			while (parent && parent !== tempDiv) {
				if (parent.tagName === "A") {
					return NodeFilter.FILTER_REJECT
				}
				parent = parent.parentElement
			}
			return NodeFilter.FILTER_ACCEPT
		},
	})

	let currentNode: Node | null
	while ((currentNode = walker.nextNode())) {
		const textContent = currentNode.textContent || ""
		const matches = textContent.match(urlRegex)
		if (matches) {
			plainTextUrls.push(...matches)
		}
	}

	plainTextUrls.forEach((url, index) => {
		// Check if this URL is already captured as an anchor tag
		const isAlreadyCaptured = extractedLinks.some((link) => link.url === url)

		if (!isAlreadyCaptured) {
			try {
				const urlObj = new URL(url)
				const displayText = urlObj.hostname.replace(/^www\./, "")
				const id = `text-${urlKey(url)}-${index}`

				extractedLinks.push({
					id,
					url,
					text: displayText,
				})
			} catch (error) {
				// Skip invalid URLs
			}
		}
	})

	// Remove duplicates based on URL
	const uniqueLinks = extractedLinks.reduce((acc, current) => {
		const exists = acc.find((item) => item.url === current.url)
		if (!exists) {
			// Use URL-based ID to ensure consistent IDs for the same URL
			const urlBasedId = `link-${urlKey(current.url)}`
			acc.push({
				...current,
				id: urlBasedId,
			})
		}
		return acc
	}, [] as ExtractedLink[])

	return uniqueLinks
}

/**
 * Debounced link extraction function
 */
export function createDebouncedLinkExtractor(delay = 300) {
	let timeoutId: NodeJS.Timeout | null = null

	return (htmlContent: string, callback: (links: ExtractedLink[]) => void) => {
		if (timeoutId) {
			clearTimeout(timeoutId)
		}

		timeoutId = setTimeout(() => {
			const links = extractLinksFromHTML(htmlContent)
			callback(links)
		}, delay)
	}
}
